import { useCallback, useEffect, useMemo, useState } from 'react';
import { useProducts } from '@/hooks/use-products';

/** The subset of a product row the promotion form needs to render a picked
 *  item. Kept flat (not the raw API shape) so both the applicable-products
 *  table and the bulk picker read from one structure. */
export interface PromotionProductRow {
  id: string;
  /** Null when the product can't be resolved (deleted, or not stocked at the
   *  active location) and the promotion payload carried no name either. */
  name: string | null;
  sku: string | null;
  primaryBarcode: string | null;
  sellingPrice: number | null;
  cost: number | null;
  inStockQty: number | null;
  unit: string | null;
  departmentName: string | null;
  imageUrl: string | null;
  /** False for rows seeded from the promotion payload (name + sku only) — they
   *  still need a products-service fetch to fill in price / UOM / barcode. */
  enriched: boolean;
}

const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'string' ? parseFloat(v) : (v as number);
  return Number.isFinite(n) ? n : null;
};

/** Map a products-service row onto the flat shape above. */
export function toPromotionProductRow(p: any): PromotionProductRow {
  return {
    id: p.id,
    name: p.name ?? null,
    sku: p.sku ?? null,
    primaryBarcode: p.primaryBarcode ?? null,
    sellingPrice: num(p.sellingPrice),
    cost: num(p.cost),
    inStockQty: num(p.inStockQty),
    unit: p.defaultSellingUnit ?? null,
    departmentName: p.departmentName ?? null,
    imageUrl: p.imageUrl ?? p.commonImageUrl ?? null,
    enriched: true,
  };
}

export function placeholderProductRow(id: string): PromotionProductRow {
  return {
    id,
    name: null,
    sku: null,
    primaryBarcode: null,
    sellingPrice: null,
    cost: null,
    inStockQty: null,
    unit: null,
    departmentName: null,
    imageUrl: null,
    enriched: false,
  };
}

/**
 * Resolves full product detail for every id the promotion has selected.
 *
 * Rows arrive from three places: the bulk picker and the type-ahead hand over
 * complete rows as the user picks them, the promotion payload seeds name/sku on
 * edit, and anything still unresolved is fetched in one `ids=` request. Ids the
 * service doesn't return get an enriched tombstone so we stop asking for them.
 */
export function useProductDirectory(active: boolean, ids: string[], locationId?: string) {
  const [rows, setRows] = useState<Record<string, PromotionProductRow>>({});

  const remember = useCallback((incoming: any[] | undefined) => {
    if (!incoming?.length) return;
    setRows((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const p of incoming) {
        if (!p?.id || next[p.id]?.enriched) continue;
        next[p.id] = toPromotionProductRow(p);
        changed = true;
      }
      return changed ? next : prev;
    });
  }, []);

  const seed = useCallback((entries: Array<{ id: string; name?: string | null; sku?: string | null }>) => {
    if (!entries.length) return;
    setRows((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const e of entries) {
        if (next[e.id]) continue;
        next[e.id] = { ...placeholderProductRow(e.id), name: e.name ?? null, sku: e.sku ?? null };
        changed = true;
      }
      return changed ? next : prev;
    });
  }, []);

  // Sorted + joined so the request key is stable regardless of pick order.
  const pendingKey = useMemo(
    () =>
      Array.from(new Set(ids))
        .filter((id) => !rows[id]?.enriched)
        .sort()
        .join(','),
    [ids, rows],
  );

  const { data } = useProducts(
    { ids: pendingKey, perPage: 500, locationId: locationId || undefined },
    { enabled: active && !!pendingKey },
  );

  // Folding the response into the cache is the point of this hook: the rows
  // have to outlive the query key, which changes every time the pending set
  // shrinks. Deriving them instead would drop a row the moment its fetch key
  // rotated, so the accumulate-in-state pattern is deliberate here. It settles
  // in one pass — once every pending id is enriched, `pendingKey` empties and
  // the query switches off.
  useEffect(() => {
    if (!data || !pendingKey) return;
    const returned = ((data as any).data as any[]) ?? [];
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setRows((prev) => {
      const next = { ...prev };
      for (const p of returned) next[p.id] = toPromotionProductRow(p);
      for (const id of pendingKey.split(',')) {
        if (!next[id]?.enriched) next[id] = { ...(next[id] ?? placeholderProductRow(id)), enriched: true };
      }
      return next;
    });
  }, [data, pendingKey]);

  return { rows, remember, seed };
}
